import { useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { AuthLayout, Button, Input } from "@shop-ledger/ui-components";
import { usePlatformAuth } from "../auth/PlatformAuthContext";

/**
 * Step 2 of §9.1's login: the MFA code. Built as a real, separate step (its own route,
 * its own submit, its own error state) rather than a second field bolted onto the login
 * form — exactly the shape a real TOTP/SMS/WebAuthn second factor needs. In real mode (the
 * default), this genuinely verifies a TOTP code against the operator's `mfa_secret`
 * server-side (`pyotp`, ±30s window) — enter the current code from whatever authenticator
 * app was set up from the `otpauth://` URI `seed_platform_operator.py` printed once at
 * provisioning time. `demoCodeHint` (below) is only ever set when `mockPlatformApiClient`
 * issued the challenge — see `PlatformAuthContext.demoCodeHint`'s doc comment.
 */
export function MfaPage() {
  const { status, verifyMfa, cancelMfa, loginPending, mfaError, demoCodeHint } = usePlatformAuth();
  const [code, setCode] = useState("");

  if (status === "unauthenticated") return <Navigate to="/login" replace />;
  if (status === "authenticated") return <Navigate to="/" replace />;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    try {
      await verifyMfa(code);
    } catch {
      // mfaError is already set by PlatformAuthContext.
    }
  }

  return (
    <AuthLayout
      eyebrow="Platform operator"
      title="Verify it's you"
      subtitle="Enter the 6-digit code from your authenticator app."
      tagline="A second factor on every operator login — one console can see every tenant, so it gets the strongest door."
      onSubmit={handleSubmit}
    >
      {demoCodeHint ? (
        <div className="demo-credentials-hint">
          <strong>Mock mode</strong> — a real MFA flow never reveals the expected code to the browser that has
          to prove it. This is shown only because <code>mockPlatformApiClient</code> is active (no real
          authenticator app to generate one from in mock mode):{" "}
          <code>{demoCodeHint}</code>
        </div>
      ) : null}
      <Input
        label="6-digit code"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        required
      />
      {mfaError ? (
        <p className="banner banner--error" role="alert">
          {mfaError}
        </p>
      ) : null}
      <div className="form-actions" style={{ flexDirection: "column", alignItems: "stretch" }}>
        <Button type="submit" variant="primary" size="md" disabled={loginPending}>
          {loginPending ? "Verifying…" : "Verify"}
        </Button>
        <Button type="button" variant="ghost" size="md" onClick={cancelMfa} disabled={loginPending}>
          Back to login
        </Button>
      </div>
    </AuthLayout>
  );
}
